import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { api, contentService } from '../services';
import { isControlPlaneHost } from '../utils/runtime';

interface SiteSettings {
  id?: number;
  site_name: string;
  site_description: string;
  primary_color?: string;
  secondary_color?: string;
  show_stats_section?: boolean;
  company_info: {
    name: string;
    cnpj: string;
    creci: string;
    phone: string;
    email: string;
    whatsapp: string;
    address: {
      street: string;
      number: string;
      complement?: string;
      neighborhood: string;
      city: string;
      state: string;
      zip_code: string;
    };
  };
  social_media: {
    facebook?: string;
    instagram?: string;
    youtube?: string;
    linkedin?: string;
  };
  business_hours: {
    weekdays: string;
    saturday: string;
    sunday: string;
  };
  seo_config: {
    default_title: string;
    title_template: string;
    default_description: string;
    keywords: string[];
    og_image?: string;
  };
  features: {
    show_hero: boolean;
    show_search: boolean;
    show_categories: boolean;
    show_featured_properties: boolean;
    show_recent_properties: boolean;
    show_stats: boolean;
    show_testimonials: boolean;
    show_blog: boolean;
    show_newsletter: boolean;
    show_whatsapp_button: boolean;
    show_partners: boolean;
  };
  customization: {
    primary_color: string;
    secondary_color: string;
    accent_color: string;
    font_family: string;
    logo_url?: string;
    favicon_url?: string;
    hero_image?: string;
    hero_title?: string;
    hero_subtitle?: string;
    watermark_settings?: any;
  };
  integrations?: {
    google_analytics_id?: string;
    google_maps_api_key?: string;
    facebook_pixel_id?: string;
  };
  settings?: any;
  
  // Campos adicionais do backend
  use_real_stats?: boolean;
  featured_categories?: number[];
  total_properties?: number;
  active_cities?: number;
  happy_clients?: number;
  years_experience?: number;
  whatsapp_message?: string;
  company_description?: string;
  
  // Homepage sections
  homepage_sections?: Array<{
    id: string;
    name: string;
    enabled: boolean;
    order: number;
  }>;
  
  // About page
  about_title?: string;
  about_content?: string;
  about_image?: string;
  about_mission?: string;
  about_vision?: string;
  about_values?: string;
  
  // Contact page
  contact_title?: string;
  contact_subtitle?: string;
  contact_address_full?: string;
  contact_hours?: string;
  contact_map_enabled?: boolean;
  contact_map_latitude?: string;
  contact_map_longitude?: string;
  google_maps_api_key?: string;
}

interface GoogleBusinessSettings {
  place_id: string;
  api_key: string;
  business_name: string;
  average_rating: number;
  total_reviews: number;
  reviews_url: string;
  last_sync?: string;
  is_active: boolean;
}

interface SettingsContextData {
  siteSettings: SiteSettings | null;
  googleBusinessSettings: GoogleBusinessSettings | null;
  loading: boolean;
  error: string | null;
  refreshSettings: () => Promise<void>;
}

const SettingsContext = createContext<SettingsContextData | undefined>(undefined);

interface SettingsProviderProps {
  children: ReactNode;
}

// Cache duration in milliseconds (5 minutes)
const CACHE_DURATION = 5 * 60 * 1000;

export const SettingsProvider: React.FC<SettingsProviderProps> = ({ children }) => {
  const [siteSettings, setSiteSettings] = useState<SiteSettings | null>(null);
  const [googleBusinessSettings, setGoogleBusinessSettings] = useState<GoogleBusinessSettings | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [lastFetched, setLastFetched] = useState<number>(0);
  const [isFetching, setIsFetching] = useState(false);

  const fetchSettings = async (forceRefresh = false) => {
    // O control plane e neutro e nao deve buscar branding ou integracoes de
    // qualquer tenant.
    if (isControlPlaneHost()) {
      setSiteSettings(null);
      setGoogleBusinessSettings(null);
      setError(null);
      setLoading(false);
      return;
    }

    // Prevent multiple simultaneous fetches
    if (isFetching) return;
    
    // Check cache validity
    const now = Date.now();
    if (!forceRefresh && lastFetched && (now - lastFetched) < CACHE_DURATION && siteSettings) {
      setLoading(false);
      return;
    }

    try {
      setIsFetching(true);
      setLoading(true);
      setError(null);

      // Fetch site settings (público)
      // Google Business settings só se houver token (autenticado)
      const token = localStorage.getItem('access_token');

      const [siteResponse, googleResponse] = await Promise.allSettled([
        contentService.getSiteSettings(),
        token ? api.get('/google-business/settings').then(res => res.data).catch(() => null) : Promise.resolve(null)
      ]);

      // Handle site settings
      if (siteResponse.status === 'fulfilled') {
        
        // Mapear settings do formato do backend para o formato esperado
        const backendSettings = siteResponse.value;
        const mappedSettings: SiteSettings = {
          id: backendSettings.id,
          site_name: backendSettings.site_title || backendSettings.company_name || 'ImobiPro',
          site_description: backendSettings.site_description || '',
          company_info: {
            name: backendSettings.company_name || 'ImobiPro',
            cnpj: '',
            creci: '',
            phone: backendSettings.phone || '',
            email: backendSettings.email || '',
            whatsapp: backendSettings.whatsapp_number || '',
            address: {
              street: backendSettings.address || '',
              number: '',
              complement: '',
              neighborhood: '',
              city: '',
              state: '',
              zip_code: ''
            }
          },
          social_media: {
            facebook: '',
            instagram: '',
            youtube: '',
            linkedin: ''
          },
          business_hours: {
            weekdays: '8:00 - 18:00',
            saturday: '8:00 - 12:00',
            sunday: 'Fechado'
          },
          seo_config: {
            default_title: backendSettings.site_title || '',
            title_template: '%s | ImobiPro',
            default_description: backendSettings.site_description || '',
            keywords: [],
            og_image: ''
          },
          features: {
            show_hero: true,
            show_search: true,
            show_categories: true,
            show_featured_properties: true,
            show_recent_properties: true,
            show_stats: backendSettings.show_stats_section !== false,
            show_testimonials: true,
            show_blog: true,
            show_newsletter: true,
            show_whatsapp_button: backendSettings.whatsapp_enabled !== false,
            show_partners: true
          },
          customization: {
            primary_color: backendSettings.primary_color || '#2563eb',
            secondary_color: backendSettings.secondary_color || '#1e40af',
            accent_color: '#e74c3c',
            font_family: 'Inter',
            logo_url: backendSettings.logo || '',
            favicon_url: '',
            hero_image: backendSettings.hero_image || '',
            hero_title: backendSettings.hero_title || '',
            hero_subtitle: backendSettings.hero_subtitle || '',
            watermark_settings: {
              enabled: backendSettings.watermark_enabled,
              logo_path: backendSettings.watermark_logo_path,
              position: backendSettings.watermark_position,
              opacity: backendSettings.watermark_opacity,
              size: backendSettings.watermark_size,
              margin: backendSettings.watermark_margin
            }
          },
          integrations: {
            google_analytics_id: '',
            google_maps_api_key: '',
            facebook_pixel_id: ''
          },
          settings: backendSettings, // Manter dados originais
          
          // Adicionar campos específicos do backend
          use_real_stats: backendSettings.use_real_stats,
          featured_categories: backendSettings.featured_categories || [],
          total_properties: backendSettings.total_properties,
          active_cities: backendSettings.active_cities,
          happy_clients: backendSettings.happy_clients,
          years_experience: backendSettings.years_experience,
          whatsapp_message: backendSettings.whatsapp_message,
          company_description: backendSettings.company_description,
          
          // Homepage sections
          homepage_sections: backendSettings.homepage_sections,
          
          // About page
          about_title: backendSettings.about_title,
          about_content: backendSettings.about_content,
          about_image: backendSettings.about_image,
          about_mission: backendSettings.about_mission,
          about_vision: backendSettings.about_vision,
          about_values: backendSettings.about_values,
          
          // Contact page
          contact_title: backendSettings.contact_title,
          contact_subtitle: backendSettings.contact_subtitle,
          contact_address_full: backendSettings.contact_address_full,
          contact_hours: backendSettings.contact_hours,
          contact_map_enabled: backendSettings.contact_map_enabled,
          contact_map_latitude: backendSettings.contact_map_latitude,
          contact_map_longitude: backendSettings.contact_map_longitude,
          google_maps_api_key: backendSettings.google_maps_api_key
        };

        setSiteSettings(mappedSettings);
      } else {
        console.error('Failed to fetch site settings:', siteResponse);
        throw new Error('Failed to fetch site settings');
      }

      // Handle Google Business settings (optional)
      if (googleResponse.status === 'fulfilled' && googleResponse.value) {
        setGoogleBusinessSettings(googleResponse.value);
      }

      setLastFetched(now);
    } catch (err) {
      console.error('Error fetching settings:', err);
      setError('Failed to load settings');
    } finally {
      setLoading(false);
      setIsFetching(false);
    }
  };

  useEffect(() => {
    fetchSettings();
  }, []);

  // Recarregar settings quando o token mudar (após login)
  useEffect(() => {
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === 'access_token' && e.newValue) {
        // Token foi adicionado, recarregar settings
        fetchSettings(true);
      }
    };

    // Listener para mudanças de storage (funciona entre tabs)
    window.addEventListener('storage', handleStorageChange);

    // Custom event para mudanças na mesma tab
    const handleTokenChange = () => {
      const token = localStorage.getItem('access_token');
      if (token && !googleBusinessSettings) {
        fetchSettings(true);
      }
    };
    window.addEventListener('auth:login', handleTokenChange);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('auth:login', handleTokenChange);
    };
  }, [googleBusinessSettings]);

  const refreshSettings = async () => {
    await fetchSettings(true);
  };

  const value: SettingsContextData = {
    siteSettings,
    googleBusinessSettings,
    loading,
    error,
    refreshSettings
  };

  return (
    <SettingsContext.Provider value={value}>
      {children}
    </SettingsContext.Provider>
  );
};

export const useSettings = () => {
  const context = useContext(SettingsContext);
  if (!context) {
    throw new Error('useSettings must be used within a SettingsProvider');
  }
  return context;
};
